import Routing from './routes';
import 'remixicon/fonts/remixicon.css';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-calendar/dist/Calendar.css';
import CommonModal from 'components/Modals';
import CommonLoader from 'components/Common/CommonLoader';
import useCommonStore from 'store/common';
import { useEffect, useState } from 'react';
import useAuthStore from 'store/auth';
import { connect, disconnect, setToken } from 'store/socket';
import useZegoCloud from 'hooks/useZegoCloud';
import { isMobile } from 'react-device-detect';
import { getFirebaseToken, messaging, requestPermission } from 'services/firebaseSDK';
import toast, { Toaster } from 'react-hot-toast';
import CustomToast from 'components/Common/CustomToast';
import { onMessage } from 'firebase/messaging';
import UsercentricsConsent from 'components/Common/UsercentricsConsent';
import './index.scss';
import { getRoomIdFromUrl } from 'utils';
import { setNewNotificationAlert } from 'store/user';
import { LANGUAGE_MAPPING, APP_IP_API } from 'constants';
import useLocalizationStore from 'store/localization';
import { useLocation, useSearchParams } from 'react-router-dom';
import { logPageView } from 'services';

function App() {
	const [searchParams] = useSearchParams();
	const requestLanguage = searchParams.get('lang');
	const { isLoading }: any = useCommonStore((state) => state);
	const { setActiveLanguage }: any = useLocalizationStore((state) => state);
	const { token: isAuthenticated }: Record<string, any> = useAuthStore(
		(state) => state.userInfo ?? {},
	);

	// Use the custom hook to manage Zendesk chat script (Commented for the face-2)
	const { createZegoLogin } = useZegoCloud();

	const [hasToken, setHasToken] = useState<boolean>(() => {
		return !!localStorage.getItem('fcmToken');
	});
	const location = useLocation(); // Get current route

	useEffect(() => {
		// Only log when on the client side
		if (typeof window !== 'undefined') {
			logPageView(location.pathname);
		}
	}, [location]); // Runs every time the route changes

	// Effect for fetching Firebase Token and requesting permissions
	useEffect(() => {
		if (isMobile) return; // Skip for mobile

		const fetchFirebaseToken = async () => {
			try {
				const firebaseToken = await getFirebaseToken();
				if (firebaseToken) {
					localStorage.setItem('fcmToken', firebaseToken);
					setHasToken(true);
				}
			} catch (err) {
				console.error('An error occurred while retrieving Firebase token.', err);
			}
		};

		// Request permission and fetch token only when needed
		if (!hasToken) {
			requestPermission();
			fetchFirebaseToken();
		} else if (!isAuthenticated) {
			fetchFirebaseToken();
		}
	}, [hasToken, isAuthenticated, setHasToken]);

	// Effect for handling incoming foreground messages
	useEffect(() => {
		const sendFCMmessage = async () => {
			try {
				const unsubscribeFunction = onMessage(messaging, (payload) => {
					if (payload?.data?.type && payload?.data?.type !== 'chat') {
						setNewNotificationAlert(true);
					}
					const currentRoomId = getRoomIdFromUrl(window.location.pathname);
					const incomingRoomId = payload?.data?.room_id || 0;

					// Notify user only if room IDs do not match
					if (
						!currentRoomId ||
						!incomingRoomId ||
						Number(currentRoomId) !== Number(incomingRoomId)
					) {
						toast.custom((t: any) => <CustomToast t={t} payload={payload} />, { duration: 5000 });
					}
				});

				return () => {
					if (unsubscribeFunction) {
						unsubscribeFunction();
					}
				};
			} catch (error) {
				console.error('Failed to setup FCM:', error);
			}
		};

		const storedLanguage = localStorage.getItem('i18nextLng');
		if (!storedLanguage || requestLanguage) {
			if (requestLanguage) {
				setActiveLanguage(requestLanguage || 'en');
			} else {
				const fetchLocale = async () => {
					try {
						const response = await fetch(APP_IP_API);
						if (!response.ok) throw new Error('Failed to fetch locale');
						const data = await response.json();

						const detectedLanguage = await detectLanguage(data.country_code);
						setActiveLanguage(detectedLanguage);
					} catch (error) {
						console.error('Error detecting locale:', error);
					}
				};
				fetchLocale();
			}
		}

		if (!isMobile) {
			sendFCMmessage();
		}
	}, []);

	useEffect(() => {
		if (isAuthenticated) {
			setToken(isAuthenticated);
			connect();
			createZegoLogin();

			return () => {
				disconnect();
			};
		}
	}, [connect, disconnect, isAuthenticated]);

	const detectLanguage = async (countryCode: string) => {
		const detectedLanguage = Object.entries(LANGUAGE_MAPPING).find(([_, countries]: any) =>
			countries.includes(countryCode),
		)?.[0];
		return detectedLanguage || 'en';
	};

	return (
		<>
			<Routing />
			<UsercentricsConsent />
			<Toaster position={'top-center'} reverseOrder={false} gutter={1} />
			{isLoading && <CommonLoader />}
			<CommonModal />
		</>
	);
}

export default App;
